import { useState, useEffect, useCallback, useRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import * as api from "@/lib/api";
import LibreriaEserciziDrawer from "@/components/lab/LibreriaEserciziDrawer";
import { exportProgressionsPDF } from "@/lib/pdf";
import WorkoutArchive from "@/components/lab/WorkoutArchive";
import { Dumbbell, Plus, Trash2, Copy, Save, Repeat, Link2, Film, Share2, ExternalLink, Loader2, Library, Check, TrendingUp, ChevronUp, ChevronDown, StickyNote, Heart, MessageCircle, FileText } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);

export const emptyAllenamento = () => ({ schede: [] });

const makeEsercizio = () => ({ id: uid(), nome: "", serie: "", ripetizioni: "", recupero: "", media: "", note: "" });
const makeSuperset = () => ({ id: uid(), superset: true, serie: "", recupero: "", a: { nome: "", ripetizioni: "", media: "", note: "" }, b: { nome: "", ripetizioni: "", media: "", note: "" } });
const makeScheda = () => ({ id: uid(), tipo: "standard", gruppo: "", giri: "", esercizi: [makeEsercizio()] });
const schedaLetter = (i) => String.fromCharCode(65 + i);

const ytId = (u) => {
  const m = (u || "").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
};
const isImgUrl = (u) => /\.(gif|png|jpe?g|webp)(\?|$)/i.test((u || "").trim());

function ExerciseMedia({ url, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const has = !!(url && url.trim());
  const yid = ytId(url);
  const showPreview = hover && has;
  return (
    <div className="relative shrink-0" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            title={has ? "Modifica GIF/Video (passa il mouse per l'anteprima)" : "Aggiungi GIF/Video"}
            className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-colors ${has ? "border-[hsl(var(--c-teal))]/50 bg-[hsl(var(--c-teal))]/15 text-[hsl(var(--c-teal))]" : "border-white/10 text-muted-foreground hover:text-primary"}`}
            data-testid={testid}
          ><Film className="h-4 w-4" /></button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 border-white/10 bg-[hsl(var(--popover))] text-foreground" data-testid={`${testid}-popover`}>
          <div className="space-y-2">
            <div className="text-xs font-semibold">Link GIF o Video esercizio</div>
            <input
              type="url"
              value={url || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Incolla link (GIF, YouTube, ...)"
              className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:border-primary/60"
              data-testid={`${testid}-input`}
            />
            {has && (
              <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                <ExternalLink className="h-3 w-3" /> Apri link
              </a>
            )}
          </div>
        </PopoverContent>
      </Popover>
      {showPreview && (
        <div className="absolute bottom-full right-0 z-50 mb-2 w-64 overflow-hidden rounded-xl border border-white/15 bg-black shadow-2xl" data-testid={`${testid}-preview`}>
          {isImgUrl(url) ? (
            <img src={url} alt="anteprima" className="block w-full" />
          ) : yid ? (
            <iframe title="anteprima" className="block aspect-video w-full" src={`https://www.youtube.com/embed/${yid}?autoplay=1&mute=1&controls=0&loop=1&playlist=${yid}`} allow="autoplay; encrypted-media" />
          ) : (
            <div className="p-3 text-center text-xs text-muted-foreground">Anteprima non disponibile.<br />Clicca il bottone e apri il link.</div>
          )}
        </div>
      )}
    </div>
  );
}

function ExerciseNote({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  const has = !!(value && value.trim());
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setDraft(value || ""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={has ? "Modifica nota esercizio" : "Aggiungi nota esercizio"}
          className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors ${has ? "border-amber-400/50 bg-amber-400/15 text-amber-400" : "border-white/10 text-muted-foreground hover:text-primary"}`}
          data-testid={testid}
        ><StickyNote className="h-4 w-4" /></button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 border-white/10 bg-[hsl(var(--popover))] text-foreground" data-testid={`${testid}-popover`}>
        <div className="space-y-2">
          <div className="text-xs font-semibold">Nota esercizio</div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            placeholder="Scrivi una nota per questo esercizio (es. tecnica, cadenza, avvertenze). Il cliente la vedrà ma non potrà modificarla."
            className="w-full resize-none rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:border-primary/60"
            data-testid={`${testid}-input`}
          />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setDraft(value || ""); setOpen(false); }} className="rounded-lg border border-white/10 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground" data-testid={`${testid}-cancel`}>Annulla</button>
            <button type="button" onClick={() => { onChange(draft); setOpen(false); toast.success("Nota salvata"); }} className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground" data-testid={`${testid}-save`}>Salva</button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function PatientComment({ comment, testid }) {
  const has = !!(comment && comment.text);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" title={has ? "Commento del paziente" : "Nessun commento del paziente"}
          className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors ${has ? "border-violet-400/60 bg-violet-400/20 text-violet-300" : "border-white/10 text-muted-foreground hover:text-violet-300"}`}
          data-testid={testid} data-has-comment={has ? "true" : "false"}>
          <MessageCircle className={`h-4 w-4 ${has ? "fill-current" : ""}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 border-white/10 bg-[hsl(var(--popover))] text-foreground" data-testid={`${testid}-popover`}>
        <div className="text-xs font-semibold">Commento del paziente</div>
        {has ? (
          <>
            <p className="mt-2 whitespace-pre-wrap break-words rounded-lg border border-violet-400/30 bg-violet-400/10 px-3 py-2 text-sm" data-testid={`${testid}-text`}>{comment.text}</p>
            {comment.at && <div className="mt-1 text-[10px] text-muted-foreground">{new Date(comment.at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</div>}
          </>
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">Il paziente non ha ancora scritto commenti. Potrà farlo dal link condiviso della scheda.</p>
        )}
      </PopoverContent>
    </Popover>
  );
}

const cloneItem = (e) =>
  e.superset
    ? { id: uid(), superset: true, serie: e.serie, recupero: e.recupero, a: { ...e.a }, b: { ...e.b } }
    : { ...e, id: uid() };

const cloneScheda = (s) => ({
  id: uid(),
  tipo: s.tipo || "standard",
  gruppo: s.gruppo,
  note: s.note || "",
  giri: s.giri || "",
  esercizi: (s.esercizi || []).map(cloneItem),
});

function MoveButtons({ onUp, onDown, canUp, canDown, testid }) {
  return (
    <div className="flex shrink-0 flex-col leading-none">
      <button type="button" onClick={onUp} disabled={!canUp} title="Sposta su" data-testid={`${testid}-up`} className="text-muted-foreground transition-colors hover:text-primary disabled:cursor-default disabled:opacity-25">
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <button type="button" onClick={onDown} disabled={!canDown} title="Sposta giù" data-testid={`${testid}-down`} className="text-muted-foreground transition-colors hover:text-primary disabled:cursor-default disabled:opacity-25">
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ExerciseNameInput({ value, onChange, onPick, library, placeholder, className, testid }) {
  const [open, setOpen] = useState(false);
  const q = (value || "").toLowerCase().trim();
  const matches = q
    ? library.filter((e) => (e.nome || "").toLowerCase().includes(q) && (e.nome || "").toLowerCase() !== q).slice(0, 6)
    : [];
  return (
    <div className="relative min-w-0 flex-1">
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className={className}
        data-testid={testid}
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-lg border border-white/15 bg-[hsl(var(--popover))] shadow-2xl" data-testid={`${testid}-suggestions`}>
          {matches.map((m) => (
            <button
              key={m.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onPick(m); setOpen(false); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-primary/15"
              data-testid={`${testid}-sugg-${m.id}`}
            >
              <Film className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--c-teal))]" />
              <span className="truncate">{m.nome}</span>
              {m.media && <span className="ml-auto text-[10px] font-bold uppercase text-[hsl(var(--c-teal))]">GIF</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const setCount = (v) => { const n = parseInt(v, 10); return n > 0 ? Math.min(n, 12) : 1; };
const normSets = (s) => (s.sets && s.sets.length ? s.sets : [{ kg: s.kg || "", reps: s.reps || "" }]);

function ProgIconButton({ active, pending, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} title="Progressioni" data-testid={`${testid}-toggle`}
      className={`relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors ${active ? "border-[hsl(199_89%_55%)]/60 bg-[hsl(199_89%_55%)]/20 text-[hsl(199_89%_65%)]" : "border-white/10 text-muted-foreground hover:text-[hsl(199_89%_65%)]"}`}>
      <TrendingUp className="h-4 w-4" />
      {pending > 0 && <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[hsl(var(--c-fat))] text-[8px] font-bold text-black">{pending}</span>}
    </button>
  );
}

function ProgPanel({ entries, serieCount, onChange, onCommit, onSave, enabled, testid }) {
  const list = entries || [];
  const icls = "rounded bg-black/30 px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-primary/40";
  const changeCommit = (next) => { onChange(next); onCommit(next); };
  const addSession = () => changeCommit([...list, { id: uid(), date: new Date().toISOString().slice(0, 10), sets: Array.from({ length: serieCount }, () => ({ kg: "", reps: "" })), confirmed: true, source: "studio" }]);
  const del = (id) => changeCommit(list.filter((s) => s.id !== id));
  const toggleConfirm = (id) => changeCommit(list.map((s) => (s.id === id ? { ...s, confirmed: !s.confirmed } : s)));
  const setDate = (id, val, commit) => { const next = list.map((s) => (s.id === id ? { ...s, date: val } : s)); commit ? changeCommit(next) : onChange(next); };
  const setSetField = (id, i, field, val, commit) => {
    const next = list.map((s) => (s.id === id ? { ...s, sets: normSets(s).map((st, idx) => (idx === i ? { ...st, [field]: val } : st)) } : s));
    commit ? changeCommit(next) : onChange(next);
  };
  return (
    <div className="mt-1.5 rounded-lg border border-white/10 bg-black/25 p-2.5" data-testid={`${testid}-panel`}>
      {!enabled && <div className="mb-2 rounded bg-[hsl(var(--c-fat))]/10 px-2 py-1.5 text-[10px] text-[hsl(var(--c-fat))]">Salva il paziente e condividi la scheda per raccogliere le progressioni del cliente.</div>}
      {list.length === 0 ? (
        <div className="py-2 text-center text-[11px] text-muted-foreground">Nessuna progressione registrata.</div>
      ) : (
        <div className="space-y-2">
          {list.map((s, si) => (
            <div key={s.id} className={`rounded-md border p-2 ${s.confirmed ? "border-white/10" : "border-[hsl(var(--c-fat))]/40 bg-[hsl(var(--c-fat))]/5"}`} data-testid={`${testid}-s${si}`}>
              <div className="mb-1.5 flex items-center gap-1.5">
                <input type="date" value={s.date || ""} onChange={(ev) => setDate(s.id, ev.target.value, false)} onBlur={() => onCommit()} className={`${icls} font-mono-num`} data-testid={`${testid}-date-${si}`} />
                {!s.confirmed && <span className="rounded bg-[hsl(var(--c-fat))]/20 px-1 py-0.5 text-[9px] font-bold text-[hsl(var(--c-fat))]">da confermare</span>}
                <button type="button" onClick={() => toggleConfirm(s.id)} title={s.confirmed ? "Confermato" : "Conferma"} data-testid={`${testid}-confirm-${si}`}
                  className={`ml-auto shrink-0 rounded p-1 ${s.confirmed ? "text-[hsl(var(--c-kcal))]" : "text-muted-foreground hover:text-[hsl(var(--c-kcal))]"}`}><Check className="h-4 w-4" /></button>
                <button type="button" onClick={() => del(s.id)} title="Elimina" data-testid={`${testid}-del-${si}`} className="shrink-0 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="space-y-1">
                {normSets(s).map((st, i) => (
                  <div key={i} className="flex items-center gap-1.5" data-testid={`${testid}-set-${si}-${i}`}>
                    <span className="w-10 shrink-0 text-[10px] font-bold uppercase text-muted-foreground">Set {i + 1}</span>
                    <input type="text" value={st.kg || ""} onChange={(ev) => setSetField(s.id, i, "kg", ev.target.value, false)} onBlur={() => onCommit()} placeholder="kg" className={`w-14 text-center font-mono-num ${icls}`} data-testid={`${testid}-kg-${si}-${i}`} />
                    <span className="text-[10px] text-muted-foreground">×</span>
                    <input type="text" value={st.reps || ""} onChange={(ev) => setSetField(s.id, i, "reps", ev.target.value, false)} onBlur={() => onCommit()} placeholder="rip" className={`w-14 text-center font-mono-num ${icls}`} data-testid={`${testid}-reps-${si}-${i}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 flex gap-1.5">
        <button type="button" onClick={addSession} className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] text-muted-foreground hover:text-primary" data-testid={`${testid}-add`}><Plus className="h-3.5 w-3.5" /> Sessione</button>
        <button type="button" onClick={onSave} disabled={!enabled} className="ml-auto inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-[11px] font-semibold text-primary-foreground disabled:opacity-50" data-testid={`${testid}-save`}><Save className="h-3.5 w-3.5" /> Salva</button>
      </div>
    </div>
  );
}


export default function AllenamentoTab({ allenamento, setAllenamento, onSave, patientName, patientId }) {
  const A = allenamento && allenamento.schede ? allenamento : emptyAllenamento();
  const schede = A.schede || [];
  const [sharing, setSharing] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [library, setLibrary] = useState([]);
  const [shareUrl, setShareUrl] = useState(null);
  const [progress, setProgress] = useState({});
  const [comments, setComments] = useState({});
  const [openProg, setOpenProg] = useState({});
  const progressRef = useRef({});
  const wid = patientId ? `wk_${patientId}` : null;
  const toggleProg = (k) => setOpenProg((o) => ({ ...o, [k]: !o[k] }));

  const refreshLibrary = useCallback(async () => {
    try { setLibrary(await api.listExerciseLibrary()); } catch (e) { console.error("Errore libreria:", e); }
  }, []);
  const refreshProgress = useCallback(async () => {
    if (!wid) { setProgress({}); progressRef.current = {}; setComments({}); return; }
    try { const r = await api.getWorkoutProgress(wid); setProgress(r.progress || {}); progressRef.current = r.progress || {}; setComments(r.comments || {}); } catch (e) { setProgress({}); progressRef.current = {}; setComments({}); }
  }, [wid]);
  useEffect(() => { refreshLibrary(); }, [refreshLibrary]);
  useEffect(() => { refreshProgress(); }, [refreshProgress]);

  const applyEntries = (exKey, entries) => {
    const next = { ...progressRef.current, [exKey]: entries };
    progressRef.current = next;
    setProgress(next);
  };
  const commit = async (exKey, entries) => {
    if (entries !== undefined) applyEntries(exKey, entries);
    if (!wid) { toast.error("Salva il paziente e condividi la scheda per salvare le progressioni"); return false; }
    try { await api.saveWorkoutProgress(wid, progressRef.current); return true; }
    catch (e) { toast.error("Errore nel salvataggio delle progressioni"); return false; }
  };
  const makeProgressProps = (exKey, serieCount) => ({
    entries: progress[exKey],
    serieCount: serieCount || 1,
    onChange: (en) => applyEntries(exKey, en),
    onCommit: (en) => commit(exKey, en),
    onSave: async () => { const ok = await commit(exKey); if (ok) toast.success("Progressioni salvate"); },
    enabled: !!wid,
  });
  const progPending = (exKey) => (progress[exKey] || []).filter((s) => !s.confirmed).length;
  const schedaKeys = (sc) => (sc.esercizi || []).flatMap((ex) => (ex.superset ? [`${ex.id}:a`, `${ex.id}:b`] : [ex.id]));
  const schedaPending = (sc) => schedaKeys(sc).reduce((n, k) => n + progPending(k), 0);
  const confirmScheda = async (sc) => {
    const next = { ...progressRef.current };
    schedaKeys(sc).forEach((k) => { if (next[k]) next[k] = next[k].map((e) => ({ ...e, confirmed: true })); });
    progressRef.current = next;
    setProgress(next);
    if (await commit()) toast.success("Progressioni della scheda confermate");
  };

  const handleShare = async () => {
    if (schede.length === 0) { toast.error("Aggiungi almeno una scheda prima di condividere"); return; }
    if (!patientId) { toast.error("Salva prima il paziente per condividere l'allenamento"); return; }
    setSharing(true);
    try {
      const { id } = await api.shareWorkout(patientName || "Cliente", A, wid);
      const base = api.PUBLIC_BASE;
      const url = `${base}/api/workout/${id}`;
      if (navigator.share) {
        await navigator.share({ title: "Allenamento personalizzato", text: `Allenamento personalizzato — ${patientName || "Cliente"}`, url });
        toast.success("Allenamento condiviso");
      } else {
        setShareUrl(url);
      }
    } catch (e) {
      if (e && e.name === "AbortError") { /* user cancelled share */ }
      else toast.error("Errore nella condivisione della scheda");
    } finally {
      setSharing(false);
    }
  };

  const setSchede = (next) => setAllenamento({ ...A, schede: next });
  const addScheda = () => setSchede([...schede, makeScheda()]);
  const removeScheda = (id) => setSchede(schede.filter((s) => s.id !== id));
  const duplicateScheda = (id) => {
    const src = schede.find((s) => s.id === id);
    if (!src) return;
    setSchede([...schede, cloneScheda(src)]);
  };
  const updateScheda = (id, patch) => setSchede(schede.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const items = (schedaId) => schede.find((s) => s.id === schedaId)?.esercizi || [];
  const addEsercizio = (schedaId) => updateScheda(schedaId, { esercizi: [...items(schedaId), makeEsercizio()] });
  const addSuperset = (schedaId) => updateScheda(schedaId, { esercizi: [...items(schedaId), makeSuperset()] });
  const removeEsercizio = (schedaId, exId) => updateScheda(schedaId, { esercizi: items(schedaId).filter((e) => e.id !== exId) });
  const moveEsercizio = (schedaId, exId, dir) => {
    const list = items(schedaId);
    const idx = list.findIndex((e) => e.id === exId);
    const to = idx + dir;
    if (idx < 0 || to < 0 || to >= list.length) return;
    const next = [...list];
    [next[idx], next[to]] = [next[to], next[idx]];
    updateScheda(schedaId, { esercizi: next });
  };
  const setEsercizio = (schedaId, exId, field, val) =>
    updateScheda(schedaId, { esercizi: items(schedaId).map((e) => (e.id === exId ? { ...e, [field]: val } : e)) });
  const setEsercizioFields = (schedaId, exId, patch) =>
    updateScheda(schedaId, { esercizi: items(schedaId).map((e) => (e.id === exId ? { ...e, ...patch } : e)) });
  const setSuperSub = (schedaId, exId, slot, field, val) =>
    updateScheda(schedaId, { esercizi: items(schedaId).map((e) => (e.id === exId ? { ...e, [slot]: { ...e[slot], [field]: val } } : e)) });
  const setSuperSubFields = (schedaId, exId, slot, patch) =>
    updateScheda(schedaId, { esercizi: items(schedaId).map((e) => (e.id === exId ? { ...e, [slot]: { ...e[slot], ...patch } } : e)) });

  const inputCls = "rounded bg-black/30 px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary/40";

  return (
    <div className="space-y-5" data-testid="tab-allenamento">
      <section className="panel p-5 sm:p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold">
            <Dumbbell className="h-5 w-5 text-primary" /> Allenamento
          </h2>
          <div className="flex items-center gap-2">
            <button onClick={() => onSave && onSave()} className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/20" data-testid="save-allenamento-btn">
              <Save className="h-4 w-4" /> Salva
            </button>
            <button onClick={() => { if (!exportProgressionsPDF(patientName, A, progressRef.current)) toast.error("Consenti i popup per aprire il PDF"); }} className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--c-fat))]/40 bg-[hsl(var(--c-fat))]/10 px-3 py-2 text-sm font-semibold text-[hsl(var(--c-fat))] transition-colors hover:bg-[hsl(var(--c-fat))]/20" data-testid="progressioni-pdf-btn">
              <FileText className="h-4 w-4" /> PROGRESSIONI
            </button>
            <WorkoutArchive patientId={patientId} patientName={patientName} current={A} onChanged={(al) => { setAllenamento(al); refreshProgress(); }} />
            <button onClick={() => setLibOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-primary" data-testid="open-libreria-btn">
              <Library className="h-4 w-4" /> Libreria
            </button>
            <button onClick={handleShare} disabled={sharing} className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--c-teal))]/40 bg-[hsl(var(--c-teal))]/10 px-3 py-2 text-sm font-medium text-[hsl(var(--c-teal))] transition-colors hover:bg-[hsl(var(--c-teal))]/20 disabled:opacity-60" data-testid="share-allenamento-btn">
              {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Condividi
            </button>
            <button onClick={addScheda} className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/20" data-testid="add-scheda-btn">
              <Plus className="h-4 w-4" /> Nuova Scheda
            </button>
          </div>
        </div>

        {schede.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/10 py-10 text-center text-sm text-muted-foreground">
            Nessuna scheda di allenamento. Crea la prima.
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {schede.map((s, si) => {
              const isCircuito = s.tipo === "circuito";
              return (
                <div key={s.id} className="rounded-xl border border-white/10 bg-black/20 p-4" data-testid={`scheda-${si}`}>
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <span className="rounded-md bg-primary/15 px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-primary" data-testid={`scheda-label-${si}`}>
                      Scheda {schedaLetter(si)}
                    </span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => duplicateScheda(s.id)} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-primary" title="Duplica scheda" data-testid={`duplicate-scheda-${si}`}>
                        <Copy className="h-4 w-4" /> Duplica
                      </button>
                      <button onClick={() => removeScheda(s.id)} className="rounded-lg border border-white/10 p-2 text-muted-foreground transition-colors hover:text-destructive" title="Elimina scheda" data-testid={`remove-scheda-${si}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="mb-3 flex gap-1 rounded-lg border border-white/10 bg-black/25 p-1">
                    {[["standard", "Standard"], ["circuito", "Circuito"]].map(([v, l]) => (
                      <button key={v} onClick={() => updateScheda(s.id, { tipo: v })} data-testid={`scheda-tipo-${si}-${v}`}
                        className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${s.tipo === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
                    ))}
                  </div>

                  <textarea
                    value={s.note || ""}
                    onChange={(e) => updateScheda(s.id, { note: e.target.value })}
                    rows={2}
                    placeholder="Note scheda (es. riscaldamento, indicazioni generali)"
                    className={`mb-3 w-full resize-y rounded-lg border px-3 py-2 text-sm outline-none focus:border-primary/60 ${(s.note || "").trim() ? "border-amber-400/50 bg-amber-400/10" : "border-white/10 bg-black/30"}`}
                    data-testid={`scheda-note-${si}`}
                  />

                  <div className="mb-3 flex justify-end">
                    <button type="button" onClick={() => confirmScheda(s)} disabled={!schedaPending(s)}
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${schedaPending(s) ? "border-[hsl(var(--c-kcal))]/50 bg-[hsl(var(--c-kcal))]/15 text-[hsl(var(--c-kcal))] hover:bg-[hsl(var(--c-kcal))]/25" : "cursor-not-allowed border-white/10 text-muted-foreground opacity-60"}`}
                      data-testid={`confirm-all-prog-${si}`}>
                      <Check className="h-3.5 w-3.5" /> Conferma tutte le progressioni{schedaPending(s) ? ` (${schedaPending(s)})` : ""}
                    </button>
                  </div>

                  <input
                    type="text"
                    value={s.gruppo}
                    onChange={(e) => updateScheda(s.id, { gruppo: e.target.value })}
                    placeholder="Nome gruppi muscolari (es. Petto & Tricipiti)"
                    className="mb-3 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm font-semibold outline-none focus:border-primary/60"
                    data-testid={`scheda-gruppo-${si}`}
                  />

                  {isCircuito && (
                    <div className="mb-3 flex items-center gap-2 rounded-lg border border-white/10 bg-black/25 px-3 py-2">
                      <Repeat className="h-4 w-4 text-primary" />
                      <span className="text-xs font-medium text-muted-foreground">Numero giri</span>
                      <input type="text" value={s.giri || ""} onChange={(e) => updateScheda(s.id, { giri: e.target.value })} placeholder="es. 3" className="ml-auto w-20 rounded bg-black/30 px-2 py-1.5 text-center font-mono-num text-sm outline-none focus:ring-1 focus:ring-primary/40" data-testid={`scheda-giri-${si}`} />
                    </div>
                  )}

                  <div className="space-y-2">
                    {(s.esercizi || []).map((ex, ei) => (
                      ex.superset ? (
                        <div key={ex.id} className="rounded-lg border border-[hsl(var(--c-fat))]/40 bg-[hsl(var(--c-fat))]/5 p-3" data-testid={`superset-${si}-${ei}`}>
                          <div className="mb-2 flex items-center justify-between">
                            <span className="inline-flex items-center gap-1 rounded bg-[hsl(var(--c-fat))]/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--c-fat))]">
                              <Link2 className="h-3 w-3" /> Super Serie
                            </span>
                            <div className="flex items-center gap-1.5">
                              <MoveButtons onUp={() => moveEsercizio(s.id, ex.id, -1)} onDown={() => moveEsercizio(s.id, ex.id, 1)} canUp={ei > 0} canDown={ei < (s.esercizi || []).length - 1} testid={`move-ex-${si}-${ei}`} />
                              <button onClick={() => removeEsercizio(s.id, ex.id)} className="text-muted-foreground hover:text-destructive" title="Rimuovi super serie" data-testid={`remove-ex-${si}-${ei}`}><Trash2 className="h-4 w-4" /></button>
                            </div>
                          </div>
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-2">
                              <span className="w-4 shrink-0 text-center text-xs font-bold text-muted-foreground">A</span>
                              <ExerciseNameInput value={ex.a.nome} onChange={(v) => setSuperSub(s.id, ex.id, "a", "nome", v)} onPick={(m) => setSuperSubFields(s.id, ex.id, "a", { nome: m.nome, media: m.media || "" })} library={library} placeholder="Esercizio A" className={`w-full ${inputCls}`} testid={`ss-a-nome-${si}-${ei}`} />
                              <ExerciseMedia url={ex.a.media} onChange={(v) => setSuperSub(s.id, ex.id, "a", "media", v)} testid={`ss-a-media-${si}-${ei}`} />
                              <ExerciseNote value={ex.a.note} onChange={(v) => setSuperSub(s.id, ex.id, "a", "note", v)} testid={`ss-a-note-${si}-${ei}`} />
                              <PatientComment comment={comments[`${ex.id}:a`]} testid={`ss-a-comment-${si}-${ei}`} />
                              <ProgIconButton active={!!openProg[`${ex.id}:a`]} pending={progPending(`${ex.id}:a`)} onClick={() => toggleProg(`${ex.id}:a`)} testid={`prog-${si}-${ei}-a`} />
                              <input type="text" value={ex.a.ripetizioni} onChange={(e) => setSuperSub(s.id, ex.id, "a", "ripetizioni", e.target.value)} placeholder="Rip." className={`w-16 text-center font-mono-num ${inputCls}`} data-testid={`ss-a-rip-${si}-${ei}`} />
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="w-4 shrink-0 text-center text-xs font-bold text-muted-foreground">B</span>
                              <ExerciseNameInput value={ex.b.nome} onChange={(v) => setSuperSub(s.id, ex.id, "b", "nome", v)} onPick={(m) => setSuperSubFields(s.id, ex.id, "b", { nome: m.nome, media: m.media || "" })} library={library} placeholder="Esercizio B" className={`w-full ${inputCls}`} testid={`ss-b-nome-${si}-${ei}`} />
                              <ExerciseMedia url={ex.b.media} onChange={(v) => setSuperSub(s.id, ex.id, "b", "media", v)} testid={`ss-b-media-${si}-${ei}`} />
                              <ExerciseNote value={ex.b.note} onChange={(v) => setSuperSub(s.id, ex.id, "b", "note", v)} testid={`ss-b-note-${si}-${ei}`} />
                              <PatientComment comment={comments[`${ex.id}:b`]} testid={`ss-b-comment-${si}-${ei}`} />
                              <ProgIconButton active={!!openProg[`${ex.id}:b`]} pending={progPending(`${ex.id}:b`)} onClick={() => toggleProg(`${ex.id}:b`)} testid={`prog-${si}-${ei}-b`} />
                              <input type="text" value={ex.b.ripetizioni} onChange={(e) => setSuperSub(s.id, ex.id, "b", "ripetizioni", e.target.value)} placeholder="Rip." className={`w-16 text-center font-mono-num ${inputCls}`} data-testid={`ss-b-rip-${si}-${ei}`} />
                            </div>
                          </div>
                          <div className="mt-2 flex items-center gap-3 border-t border-white/5 pt-2">
                            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">Serie
                              <input type="text" value={ex.serie} onChange={(e) => setEsercizio(s.id, ex.id, "serie", e.target.value)} placeholder="4" className={`w-14 text-center font-mono-num ${inputCls}`} data-testid={`ss-serie-${si}-${ei}`} />
                            </label>
                            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">Recupero
                              <input type="text" value={ex.recupero} onChange={(e) => setEsercizio(s.id, ex.id, "recupero", e.target.value)} placeholder="90s" className={`w-16 text-center font-mono-num ${inputCls}`} data-testid={`ss-recupero-${si}-${ei}`} />
                            </label>
                          </div>
                          <div className="mt-2 space-y-1 border-t border-white/5 pt-2">
                            {openProg[`${ex.id}:a`] && <div className="flex items-start gap-2"><span className="w-4 pt-1 text-center text-xs font-bold text-muted-foreground">A</span><div className="flex-1"><ProgPanel {...makeProgressProps(`${ex.id}:a`, setCount(ex.serie))} testid={`prog-${si}-${ei}-a`} /></div></div>}
                            {openProg[`${ex.id}:b`] && <div className="flex items-start gap-2"><span className="w-4 pt-1 text-center text-xs font-bold text-muted-foreground">B</span><div className="flex-1"><ProgPanel {...makeProgressProps(`${ex.id}:b`, setCount(ex.serie))} testid={`prog-${si}-${ei}-b`} /></div></div>}
                          </div>
                        </div>
                      ) : (
                        <div key={ex.id} data-testid={`esercizio-${si}-${ei}`}>
                          <div className="flex items-center gap-2">
                            <ExerciseNameInput value={ex.nome} onChange={(v) => setEsercizio(s.id, ex.id, "nome", v)} onPick={(m) => setEsercizioFields(s.id, ex.id, { nome: m.nome, media: m.media || "" })} library={library} placeholder="Nome esercizio" className={`w-full ${inputCls}`} testid={`ex-nome-${si}-${ei}`} />
                            <ExerciseMedia url={ex.media} onChange={(v) => setEsercizio(s.id, ex.id, "media", v)} testid={`ex-media-${si}-${ei}`} />
                            <ExerciseNote value={ex.note} onChange={(v) => setEsercizio(s.id, ex.id, "note", v)} testid={`ex-note-${si}-${ei}`} />
                            <PatientComment comment={comments[ex.id]} testid={`ex-comment-${si}-${ei}`} />
                            <button type="button" onClick={() => setEsercizio(s.id, ex.id, "cardio", !ex.cardio)} title={ex.cardio ? "Esercizio cardio (clicca per tornare a pesi)" : "Imposta come esercizio cardio"} data-testid={`ex-cardio-${si}-${ei}`}
                              className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors ${ex.cardio ? "border-rose-400/50 bg-rose-400/15 text-rose-400" : "border-white/10 text-muted-foreground hover:text-rose-400"}`}>
                              <Heart className={`h-4 w-4 ${ex.cardio ? "fill-current" : ""}`} />
                            </button>
                            {ex.cardio ? (
                              <>
                                <input type="text" value={ex.tempo || ""} onChange={(e) => setEsercizio(s.id, ex.id, "tempo", e.target.value)} placeholder="Tempo" title="Tempo (es. 20 min)" className={`w-20 text-center font-mono-num ${inputCls}`} data-testid={`ex-tempo-${si}-${ei}`} />
                                <input type="text" value={ex.bpm || ""} onChange={(e) => setEsercizio(s.id, ex.id, "bpm", e.target.value)} placeholder="BPM" title="Battiti al minuto (es. 130-145)" className={`w-20 text-center font-mono-num ${inputCls}`} data-testid={`ex-bpm-${si}-${ei}`} />
                              </>
                            ) : (<>
                            <ProgIconButton active={!!openProg[ex.id]} pending={progPending(ex.id)} onClick={() => toggleProg(ex.id)} testid={`prog-${si}-${ei}`} />
                            {!isCircuito && <input type="text" value={ex.serie} onChange={(e) => setEsercizio(s.id, ex.id, "serie", e.target.value)} placeholder="Serie" title="Serie" className={`w-14 text-center font-mono-num ${inputCls}`} data-testid={`ex-serie-${si}-${ei}`} />}
                            <input type="text" value={ex.ripetizioni} onChange={(e) => setEsercizio(s.id, ex.id, "ripetizioni", e.target.value)} placeholder="Rip." title="Ripetizioni" className={`w-16 text-center font-mono-num ${inputCls}`} data-testid={`ex-ripetizioni-${si}-${ei}`} />
                            {!isCircuito && <input type="text" value={ex.recupero} onChange={(e) => setEsercizio(s.id, ex.id, "recupero", e.target.value)} placeholder="Rec." title="Recupero" className={`w-16 text-center font-mono-num ${inputCls}`} data-testid={`ex-recupero-${si}-${ei}`} />}
                            </>)}
                            <button onClick={() => removeEsercizio(s.id, ex.id)} className="shrink-0 text-muted-foreground hover:text-destructive" title="Rimuovi esercizio" data-testid={`remove-ex-${si}-${ei}`}><Trash2 className="h-4 w-4" /></button>
                            <MoveButtons onUp={() => moveEsercizio(s.id, ex.id, -1)} onDown={() => moveEsercizio(s.id, ex.id, 1)} canUp={ei > 0} canDown={ei < (s.esercizi || []).length - 1} testid={`move-ex-${si}-${ei}`} />
                          </div>
                          {openProg[ex.id] && !ex.cardio && <ProgPanel {...makeProgressProps(ex.id, setCount(ex.serie))} testid={`prog-${si}-${ei}`} />}
                        </div>
                      )
                    ))}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button onClick={() => addEsercizio(s.id)} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:text-primary" data-testid={`add-ex-${si}`}>
                      <Plus className="h-4 w-4" /> Aggiungi esercizio
                    </button>
                    {!isCircuito && (
                      <button onClick={() => addSuperset(s.id)} className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--c-fat))]/40 px-3 py-2 text-xs font-medium text-[hsl(var(--c-fat))] transition-colors hover:bg-[hsl(var(--c-fat))]/10" data-testid={`add-superset-${si}`}>
                        <Link2 className="h-4 w-4" /> Aggiungi super serie
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <LibreriaEserciziDrawer open={libOpen} setOpen={setLibOpen} onChanged={refreshLibrary} />

      <Dialog open={!!shareUrl} onOpenChange={(v) => { if (!v) setShareUrl(null); }}>
        <DialogContent className="border-white/10 bg-[hsl(var(--card))] text-foreground sm:max-w-sm" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Share2 className="h-5 w-5 text-[hsl(var(--c-teal))]" /> Allenamento personalizzato
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">La scheda è pronta. Apri o copia il link per inviarlo al cliente.</p>
          <div className="mt-2 flex flex-col gap-2">
            <a href={shareUrl || "#"} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5" data-testid="share-open-btn">
              <ExternalLink className="h-4 w-4" /> Apri allenamento
            </a>
            <button
              onClick={async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copiato"); } catch (e) { toast.error("Copia non riuscita"); } }}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-white/10 px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
              data-testid="share-copy-btn"
            >
              <Copy className="h-4 w-4" /> Copia link
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
